import { ComponentProps, FC, memo, useMemo } from 'react'

import { useFragment } from '@apollo/client'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import { ExpensesExpensableApprovableFragmentDoc } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { EmployeeTableViewRow } from 'modules/inbox_tasks/activities/Task/utils/content'

import ExpenseExpensableDetails from './Expense'
import MileageExpensableDetails from './Mileage'
import PerDiemExpensableDetails from './PerDiem'

type ExpensableExpenseTaskContentProps = {
  id: number
  requesterContent: EmployeeTableViewRow
}

const ExpensableExpenseTaskContent: FC<ExpensableExpenseTaskContentProps> = ({
  id,
  requesterContent,
}) => {
  const { navigation } = useNavigation()

  const {
    access: { locale: currentAccessLocale },
  } = useCurrentSession()

  const { data } = useFragment({
    fragment: ExpensesExpensableApprovableFragmentDoc,
    fragmentName: 'ExpensesExpensableApprovable',
    from: {
      __typename: 'ExpensesExpensable',
      id,
    },
    optimistic: false,
  })

  const locale = currentAccessLocale

  const info = useMemo(
    () => ({
      amount: data.amount
        ? MoneyWithCurrency({
            amount: Number(data.amount),
            currency: data.currency,
            locale,
          })
        : undefined,
      reimbursableAmount: data.reimbursableAmount
        ? MoneyWithCurrency({
            amount: Number(data.reimbursableAmount),
            currency: data.reimbursableCurrency,
            locale,
          })
        : undefined,
      effectiveOn: dateBackendDateTimeToUI(data.effectiveOn),
      currentAccessLocale,
    }),
    [data, locale, currentAccessLocale]
  )

  const commonDetails: ComponentProps<typeof DetailsItemsList>['details'] = useMemo(
    () => [...(requesterContent ? [requesterContent] : [])],
    [requesterContent]
  )

  const typeComponentProps = useMemo(
    () => ({
      id,
      commonDetails,
      moreDetails: {
        title: i18n.t('tasks.view_more_details'),
        url: navigation.expenses.list.expenses.expenseId.fullPath({
          expense_id: id,
        }),
      },
      info,
    }),
    [id, commonDetails, navigation, info]
  )

  if (!data) {
    return null
  }

  switch (data.type) {
    case 'expense':
      return <ExpenseExpensableDetails {...typeComponentProps} />
    case 'mileage':
      return <MileageExpensableDetails {...typeComponentProps} />
    case 'perdiem':
      return <PerDiemExpensableDetails {...typeComponentProps} />
    default:
      return null
  }
}

export default memo(ExpensableExpenseTaskContent)

export * from './metadata'
